import { ApiError, type ScopeType } from '@fc/core'
import { Badge, Button, Card, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Mail, Send, Trash2 } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  createInvitation,
  listInvitations,
  listRoles,
  listScopes,
  revokeInvitation,
  type RoleSummary,
} from '../../lib/members-api'
import {
  describeScope,
  INVITATION_STATUS_LABEL,
  INVITATION_STATUS_VARIANT,
  SCOPE_LABEL,
} from './shared'

const selectClass =
  'h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring disabled:opacity-50'

function InviteForm() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [roleId, setRoleId] = useState('')
  const [scopeRefId, setScopeRefId] = useState('')
  const [link, setLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const rolesQuery = useQuery({ queryKey: ['members', 'roles'], queryFn: listRoles })
  const scopesQuery = useQuery({ queryKey: ['members', 'scopes'], queryFn: listScopes })

  const selectedRole: RoleSummary | undefined = useMemo(
    () => rolesQuery.data?.find((r) => r.id === roleId),
    [rolesQuery.data, roleId],
  )
  const scopeType = (selectedRole?.scopeLevel ?? 'org') as ScopeType
  const needsScopeRef = scopeType !== 'org'

  const scopeOptions = useMemo(() => {
    if (!scopesQuery.data) return []
    if (scopeType === 'site') return scopesQuery.data.sites
    if (scopeType === 'team') return scopesQuery.data.teams
    if (scopeType === 'group') return scopesQuery.data.groups
    return []
  }, [scopesQuery.data, scopeType])

  const mutation = useMutation({
    mutationFn: () =>
      createInvitation({
        email,
        roleId,
        scopeType,
        siteId: scopeType === 'site' ? scopeRefId : null,
        teamId: scopeType === 'team' ? scopeRefId : null,
        groupId: scopeType === 'group' ? scopeRefId : null,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      const url = `${window.location.origin}/invite/${encodeURIComponent(res.token)}`
      setLink(url)
      setCopied(false)
      setEmail('')
      setRoleId('')
      setScopeRefId('')
    },
  })

  const canSubmit =
    email.trim() !== '' &&
    roleId !== '' &&
    (!needsScopeRef || scopeRefId !== '') &&
    !mutation.isPending

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (canSubmit) mutation.mutate()
  }

  async function copyLink() {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Card className="space-y-4 p-5">
      <div className="flex items-center gap-2">
        <Mail className="size-4 text-secondary" />
        <h2 className="font-display text-base font-semibold">Invite a new member</h2>
      </div>

      <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="invite-email">Email</Label>
          <Input
            id="invite-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@church.org"
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="invite-role">Role</Label>
          <select
            id="invite-role"
            className={selectClass}
            value={roleId}
            onChange={(e) => {
              setRoleId(e.target.value)
              setScopeRefId('')
            }}
            required
          >
            <option value="">Select a role…</option>
            {rolesQuery.data?.map((r) => (
              <option key={r.id} value={r.id} disabled={!r.delegable}>
                {r.name}
                {!r.delegable ? ' (beyond your permissions)' : ''}
              </option>
            ))}
          </select>
        </div>

        {needsScopeRef && (
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="invite-scope">{SCOPE_LABEL[scopeType]}</Label>
            <select
              id="invite-scope"
              className={selectClass}
              value={scopeRefId}
              onChange={(e) => setScopeRefId(e.target.value)}
              required
            >
              <option value="">Select a {SCOPE_LABEL[scopeType].toLowerCase()}…</option>
              {scopeOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {mutation.isError && (
          <p className="text-sm text-danger sm:col-span-2" role="alert">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Could not send invite.'}
          </p>
        )}

        <div className="sm:col-span-2">
          <Button type="submit" disabled={!canSubmit}>
            {mutation.isPending ? <Spinner /> : <Send className="size-4" />}
            Create invitation
          </Button>
        </div>
      </form>

      {link && (
        <div className="space-y-2 rounded-lg border border-success/40 bg-success/10 p-3">
          <p className="text-sm font-medium text-success">Invitation created.</p>
          <p className="text-xs text-muted">
            Share this one-time link with the invitee. It won’t be shown again.
          </p>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={link}
              className="font-mono text-xs"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button type="button" variant="outline" size="sm" onClick={copyLink}>
              <Copy className="size-4" />
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

export function InvitationsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['invitations'],
    queryFn: listInvitations,
  })

  const revoke = useMutation({
    mutationFn: (id: string) => revokeInvitation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invitations'] }),
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invitations"
        description="Invite new people to your workspace with a specific role and scope."
        actions={
          <Button asChild variant="outline">
            <Link to="/app/members">Back to members</Link>
          </Button>
        }
      />

      <InviteForm />

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted">
            <Spinner /> Loading invitations…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-16 text-center">
            <p className="text-sm text-danger">Couldn’t load invitations.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="space-y-2 py-16 text-center">
            <Mail className="mx-auto size-8 text-dim" aria-hidden="true" />
            <p className="text-sm text-muted">No invitations yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Email</th>
                  <th className="px-5 py-3 font-semibold">Role &amp; scope</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Invited by</th>
                  <th className="px-5 py-3 font-semibold">Expires</th>
                  <th className="px-5 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((inv) => (
                  <tr key={inv.id} className="border-b border-border-soft last:border-0">
                    <td className="px-5 py-3 font-medium">{inv.email}</td>
                    <td className="px-5 py-3 text-muted">
                      {inv.roleName ?? '—'}
                      <span className="block text-xs text-dim">
                        {describeScope(inv.scopeType, inv.scopeLabel)}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={INVITATION_STATUS_VARIANT[inv.status]}>
                        {INVITATION_STATUS_LABEL[inv.status]}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-muted">{inv.invitedByName ?? '—'}</td>
                    <td className="px-5 py-3 text-muted">
                      {new Date(inv.expiresAt).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end">
                        {inv.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => revoke.mutate(inv.id)}
                            disabled={revoke.isPending}
                            title="Revoke invitation"
                          >
                            <Trash2 className="size-4 text-danger" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
